import { formatDateOnly, formatDecimalString } from '@/lib/format';
import type {
  Target,
  TargetBillingRegion,
  TargetFxMode,
  TargetMetric,
  TargetPeriod,
  TargetPeriodKind,
  TargetSubjectType,
  TargetTranslation,
} from '@/lib/types';

/*
 * The target vocabulary — ONE copy.
 *
 * Everything that has to answer "is this metric money?" comes through here.
 * That question is asked in at least four places (whether the currency picker
 * renders, what `baseCurrency` is sent as, how a stored figure is displayed,
 * and what a validation message says), and the whole point of a single module
 * is that those four cannot disagree about one row.
 *
 * ⚠️ **Never re-derive it by matching on the metric string.** `won_deals`
 * contains "deals" and `booked` contains "book"; a `includes()` test over any
 * of them is the same defect this codebase has already shipped three times —
 * `"Unpaid"` read as settled, `"Not Accepted"` painted as accepted, a product
 * rename switching campaign inclusions off. Here the union is a real contract
 * rather than admin-editable free text, so exact membership of `MONEY_METRICS`
 * is both cheap and correct; containment is forbidden.
 *
 * ⚠️ **This module holds no attainment maths — but the REASON below was wrong,
 * and it is corrected rather than deleted because it was blocking real work.**
 *
 * The currency question is ANSWERED: nothing is ever converted, so a figure is
 * one currency by construction and a bar over a mixed-currency sum is not even
 * expressible.
 *
 * 🔴 **CORRECTED 2026-08-31, and the correction is load-bearing on the copy
 * beside every figure. SUPERSEDED:** *"a target carries one base currency, only
 * documents raised in that currency count toward it … The unique key includes
 * the currency, so one person may legitimately hold a GBP target and a USD
 * target for the same metric and period."* The second sentence is true and the
 * FIRST DOES NOT FOLLOW FROM IT. Measured in the backend's
 * `targets.service.ts` on 2026-08-31: `computeActuals` is called ONCE per
 * response with the server's `DEFAULT_BASE_CURRENCY` constant, and every money
 * metric filters `(currency IS NULL OR currency = :baseCurrency)` against that
 * one value. The `baseCurrency` on a metric row is a different fact — it is
 * read off the STORED TARGET ROW (`stored?.baseCurrency ?? null`).
 *
 * So a target held in another currency is measured against a sum taken in the
 * server's, and **nothing on the wire says which currency that sum was taken
 * in.** That is why `excludedByCurrencyNote` names no currency at all: the row
 * carries a code that is right about the commitment and wrong about the
 * exclusion, and using it would print a confident sentence about the wrong
 * currency. A document carrying NO currency counts toward every figure, by a
 * deliberate `IS NULL` in the same filter.
 *
 * 🔴 **SUPERSEDED 2026-08-31, every clause of it:** *"What blocks it now is that
 * NOTHING COMPUTES THE ACTUALS, and there is no DATA behind one either …
 * `/targets` is CRUD only … the columns a bar would be computed FROM —
 * `creditedToId`, `recognisedOn`, `baseAmount` … are written by nothing …
 * `won_deals` … carries no date binding it to a period … a bar built now would
 * render 0% on every target."*
 *
 * **`GET /targets/actuals` computes them**, for all five metrics, and this app
 * already reads it — `api.targetActuals` → `use-my-targets.ts` →
 * `UniversityPartnershipsPage`. The recognition columns the old text was waiting
 * on were **abandoned**, not filled in: the owner's 2026-08-30 decision replaced
 * them with live resolution (`COALESCE(document.account_lead_id,
 * company.account_lead_id)`) and dated each metric by a column the record already
 * carries — `issue_date`, `bill_date`, `closing_date` — because a
 * `recognised_on` stamp could never be applied to a record that reached its
 * terminal state before the column existed, and no backfill was possible. So
 * `won_deals` IS attributable to a period — by its closing date, else the date
 * it was won, and the server fills an empty closing date when a deal reaches a
 * won stage (owner decision 2026-10-15). *SUPERSEDED: "by `closing_date`"
 * alone.*
 *
 * ⚠️ **The lesson, which is why this stays here: a negative claim has no natural
 * moment of contradiction.** Nothing fails when "there is no such query" stops
 * being true — no build breaks, no type moves — so it must be re-measured on
 * purpose, and it was instead re-read and believed. This one was also written as
 * a *correction* of an earlier over-claim, and over-shot: a wrong over-claim is
 * caught by the first person who tries it, while a wrong denial is obeyed, and
 * obedience produces no evidence.
 *
 * **What survives unchanged, and still governs this module:** targets are
 * rendered as STATED FIGURES here, because attainment belongs beside the actuals
 * that answer it. Never `0`, `0%`, an empty bar or a dash where a figure is
 * merely unknown — every one of those is indistinguishable from "you have
 * achieved nothing", and `target: null` (nobody committed) is a different state
 * from `actual: '0.00'` (nothing closed, a real answer). And do NOT put
 * attainment maths in this module unused: an unused function returning a
 * plausible figure is precisely what somebody wires up later without checking
 * what it is computed from.
 */

/**
 * THE ONE CURRENCY THE DASHBOARDS SPEAK.
 *
 * Owner's decision, 2026-09-02: *"all figures should resolve to GBP only and
 * there should not be a multicurrency thing at all"* on the Management and
 * University Partnerships dashboards.
 *
 * ⚠️ **This is a DISPLAY and REQUEST decision, NOT a conversion.** Nothing
 * anywhere in EMERGE converts a foreign amount, and this constant does not
 * either. The API never converts: a document raised in another currency is
 * simply **not counted** towards a figure, which is what
 * `TargetActual.excludedByCurrency` reports on the wire. So a figure labelled
 * GBP here is a sum that was only ever taken over GBP records — the label is
 * newly honest, not newly derived.
 *
 * 🔴 **FORWARD NOTE, 2026-09-13: CONVERSION IS ARRIVING, AND THE SENTENCE
 * "Nothing anywhere in EMERGE converts a foreign amount" IS ON ITS WAY OUT.** A
 * conversion feature is being built this session — backend `f_exchange_rates`,
 * migration 178 — so the paragraph above is a reading of today, not a property
 * of the system. **The constant itself does not change and is still the
 * default.** What moves is the REASON a figure here can be trusted to be GBP:
 * today it is GBP because nothing else was COUNTED, and afterwards it may be GBP
 * because something was CONVERTED into it. Those are different facts about the
 * same number, and only the second one can be wrong about an exchange rate.
 *
 * ⚠️ **Do not reword anything above on the strength of this note, and do not
 * treat the note as the change having landed.** A migration existing is not a
 * rate being applied — this file has been wrong twice in the other direction by
 * inheriting a "not yet" past its expiry, so the claim goes when a figure on
 * this dashboard has actually been converted, measured, not when the table is
 * created. Until then every sentence above is still true.
 *
 * 🔴 **THIS FILE NOW STATES BOTH THINGS AT ONCE, AND THAT IS DELIBERATE RATHER
 * THAN AN OVERSIGHT — read which moment each one describes.**
 * `CONVERSION_APPROXIMATE_NOTE` below asserts that foreign documents ARE
 * converted. This note says they are not. Both are accurate, about different
 * days: the conversion is **written and not deployed** as of 2026-09-13, and
 * the disclaimer ships in the same release as the server that does the
 * converting, so neither is ever on screen without the other.
 *
 * ⛔ **THE ORDER IS NOT OPTIONAL, AND GETTING IT WRONG IS SILENT.** Migration
 * 178 has to apply AND the rate table has to be populated before the converting
 * SQL serves a request. An empty rate table does not error — every document
 * fails its rate lookup and is counted as unconvertible, which degrades to
 * exactly the old filtering behaviour and looks like nothing changed. So the
 * failure mode of deploying out of order is **a dashboard that quietly reports
 * what it always did**, under a sentence promising it converts.
 *
 * ⚠️ **WHAT CLOSES THIS NOTE IS AN OBSERVATION, NOT A DATE**: a figure on this
 * dashboard measurably larger than the same figure filtered, with
 * `conversion.complete` true on the response. When somebody sees that, delete
 * this block and the superseded paragraph above it together — not one of them.
 *
 * ⚠️ **Never use it to relabel a figure that names its own currency.** Where a
 * row carries a real code, that code wins; this is the fallback for a money
 * figure the wire left `null`, which on a GBP-only dashboard can only mean GBP.
 */
export const DASHBOARD_CURRENCY = 'GBP';

/**
 * 🔴 **THE METRICS A TARGET MAY BE SET AGAINST — and since 2026-09-11 that is
 * NOT the same list as the one the dashboards display.**
 *
 * *SUPERSEDED: "Display order, and the order the picker offers."* It is still
 * the order the target-setting pickers offer (`UserTargetsModal`,
 * `GroupTargetsCard`), and it is no longer what the funnel shows — see
 * `FUNNEL_METRICS` below.
 *
 * ✅ **`invoiced` IS SETTABLE, SINCE MIGRATION 162 DEPLOYED ON 2026-09-11.**
 *
 * *SUPERSEDED, and true for about two hours the same day: "`invoiced` IS
 * DELIBERATELY ABSENT AND THAT IS A MEASUREMENT, NOT AN OVERSIGHT … Adding it
 * here would put a row in two target forms whose every save is a 422 naming a
 * field the user cannot correct."* That reading was correct against the server
 * as it stood, and it carried its own instruction — re-run the probe, with its
 * control, before moving it. That was done rather than assumed:
 *
 * ```
 *                                   before 162   after 162
 * GET /targets?metric=invoiced          422         200
 * GET /targets?metric=booked            200         200
 * GET /targets?metric=bogus_metric      422         422     <- the control
 * ```
 *
 * ⚠️ **The bogus control is what makes the `invoiced` line evidence rather than
 * a coincidence.** Without it, a server that had simply stopped validating
 * `metric` at all would produce the identical 200 and read as success.
 *
 * The write side was driven too, not merely the query: a real `invoiced` target
 * was POSTed (201), and the same POST with `baseCurrency: null` was refused
 * **422** — so the CHECK was re-added with `invoiced` in its MONEY branch rather
 * than dropped or widened into an ELSE. The probe row was deleted (204, then
 * 404).
 *
 * ⚠️ **`booked` STAYS, and it is still a real commitment somebody holds.**
 * Measured the same day: target 1 is 100,000.00 GBP of `booked` for one user.
 * It left the FUNNEL; it did not leave the product.
 */
export const TARGET_METRICS: readonly TargetMetric[] = [
  /*
   * Ordered as the funnel reads, so the picker matches the dashboard beside
   * it: what is in play, what has been quoted, what was accepted, what has
   * been paid, and how many deals closed.
   */
  'pipeline',
  'quoted',
  'booked',
  'invoiced',
  'collected',
  'won_deals',
];

/**
 * 🔴 **THE FUNNEL THE DASHBOARDS DISPLAY — owner's decision, 2026-09-11.**
 *
 * ```
 * Pipeline -> Quoted -> Invoiced -> Collected -> Won deals
 * ```
 *
 * ⚠️ **IT DIFFERS FROM `TARGET_METRICS` IN EXACTLY TWO PLACES AND BOTH ARE
 * DECIDED:** `invoiced` is here and not there because it cannot be targeted
 * yet (the probe above); `booked` is there and not here because it measures
 * ACCEPTED QUOTES and no longer owns the word "Invoiced".
 *
 * 🔴 **WHY THE SPLIT HAD TO HAPPEN AT ALL, measured on production 2026-09-11,
 * organisation-wide over F.Y. 2026 - 27:**
 *
 * ```
 * booked     GBP 0.00        USD 0.00      one accepted quote exists and it is EUR
 * collected  GBP 96,365.00   USD 1,036,837.05
 * ```
 *
 * So a tile reading **"INVOICED £0"** sat beside **"COLLECTED £96.4K"** — money
 * collected against invoices that the tile above said had never been raised.
 * The figure was correct and its label was not, which is the failure this
 * file's own comment predicted: *"a figure that disagrees with its own label is
 * survivable while nothing computes it, and stops being survivable the moment
 * something does."*
 *
 * ⚠️ **A METRIC IN THIS LIST THE SERVER DOES NOT RETURN RENDERS AS NO FIGURE,
 * NEVER AS ZERO** — see `TileFigures`. That is the live state during the
 * server-then-client deploy window this list was written in.
 */
export const FUNNEL_METRICS = [
  'pipeline',
  'quoted',
  'invoiced',
  'collected',
  'won_deals',
] as const satisfies readonly TargetMetric[];

/**
 * A metric the funnel actually shows.
 *
 * ⚠️ **A NARROW LITERAL UNION RATHER THAN `TargetMetric`, AND IT IS THE ONE
 * THING STOPPING A TYPE LIE.** The dashboards build one roll-up per funnel
 * metric and key it by this type. Typed `Record<TargetMetric, ...>` that map
 * would claim to hold `booked` — which the funnel no longer builds — so
 * `rolls.booked` would typecheck and be `undefined` at runtime. That is this
 * repo's *"a TYPE asserting what the runtime lacks"* shape, and the `as const`
 * above is what makes the narrower key available to say instead.
 */
export type FunnelMetric = (typeof FUNNEL_METRICS)[number];

/**
 * The metrics denominated in money.
 *
 * This set is the whole rule. `baseCurrency` is REQUIRED for a member and MUST
 * BE NULL for a non-member, enforced server-side against the post-merge state —
 * so a row that is wrong here is not a cosmetic slip, it is a 422 the user
 * cannot act on because it names a field the form never showed them.
 */
const MONEY_METRICS: ReadonlySet<TargetMetric> = new Set<TargetMetric>([
  'booked',
  'collected',
  'quoted',
  'pipeline',
  /*
   * ⚠️ **`invoiced` is money even though no target may be set against it
   * today.** This set answers "how is this figure FORMATTED and does it carry a
   * currency", which is a fact about the metric and not about whether it is
   * settable. Leaving it out would render an invoiced total as a bare count.
   */
  'invoiced',
]);

/** Whether this metric carries a `baseCurrency`. The single decision point. */
export function metricIsMoney(metric: TargetMetric): boolean {
  return MONEY_METRICS.has(metric);
}

/**
 * Send the slug, render the label — the same rule as roles.
 *
 * Casing the slug would give "Won_deals", and splitting on the underscore would
 * give "Won deals", which reads as a state rather than as a count of records.
 */
/**
 * WHAT EACH METRIC ACTUALLY MEASURES — the server's definitions, mirrored here
 * because a label on a dashboard is a claim about money and the two files that
 * make that claim are in different repositories.
 *
 *     booked      the EX-VAT value of quotes the customer ACCEPTED
 *     invoiced    the EX-VAT value of invoices BILLED in the period (awaiting
 *                 payment, paid, credited) MINUS approved credit notes dated in
 *                 the period — raised, not necessarily paid; may be negative
 *     collected   the EX-VAT share of cash actually PAID on invoices
 *     won_deals   deals reaching a won stage (count, not money)
 *     quoted      the EX-VAT value of quotes ISSUED in the period, whatever
 *                 their outcome — a quote later declined still counts as quoted
 *     pipeline    the RAW value of open deals: every deal not won or lost, at
 *                 FULL value, UNWEIGHTED by probability (deal lines carry no tax)
 *
 * 🔴 **EX-VAT AND NET OF CREDIT NOTES SINCE owner 2026-10-28** — Xero's accrual
 * P&L basis. *SUPERSEDED: every money metric summed `grand_total`, i.e. incl.
 * tax and gross of credit notes*, which put the 2025-26 Invoiced tile ~US$267k
 * above Xero's P&L. Credited invoices still count; their credit note is what
 * nets them, so never also drop Credited invoices (that double-deducts).
 *
 * ⚠️ **`invoiced` AND `collected` ARE NOT THE SAME MONEY AND `invoiced` IS
 * NOT A SUPERSET OF IT EITHER.** One is dated by the bill date and the other by
 * payment, so an invoice raised in March and paid in May is in a different
 * period under each. Do not reconcile the two by expecting one to exceed the
 * other in a given window.
 *
 * ⚠️ **`pipeline` IS NOT WEIGHTED, and the difference is not a rounding.**
 * Three 100,000 deals at 25%, 40% and 50% are 115,000 weighted and 300,000
 * raw. The owner chose raw on 2026-08-29: it measures what is IN PLAY, not
 * what is expected to land. The deal `probability` column is not read by it.
 *
 * ⚠️ **Nothing in this app now says "weighted pipeline", and the note that used
 * to sit here is closed rather than deleted.** `DashboardPage` labelled a
 * series that way; the page was deleted along with its invented data, so the
 * clash it warned about is gone with it — **by the removal of the other side,
 * not because anybody reconciled the two.** The warning still applies to the
 * next chart: a series labelled "pipeline" must be the RAW figure above, and
 * one showing a probability-weighted number needs a different word, or two
 * figures differing by nearly three times end up under one label.
 *
 * ⚠️ `quoted` and `pipeline` went live on 2026-08-29, reversing migration
 * 086's "context only, no target". Verified on the wire before being mirrored
 * here: both are accepted as a `?metric=` filter, and a deliberately
 * incomplete POST is refused for the MISSING fields rather than for the
 * metric — which is the probe that separates "accepted" from "recognised".
 *
 * ⚠️ Both are MONEY, so both require `baseCurrency`. A money metric absent
 * from `MONEY_METRICS` would be offered without a currency picker and refused
 * on save.
 */
export const TARGET_METRIC_LABELS: Record<TargetMetric, string> = {
  /*
   * 🔴 **THE 2026-08-29 WORDING DECISION IS REVERSED — owner, 2026-09-11.**
   *
   * *SUPERSEDED: `booked: 'Invoiced revenue'`, chosen "so the target reads the
   * same as the funnel tile beside it", under a comment opening* ⚠️ **"THIS
   * LABEL DOES NOT DESCRIBE WHAT THE FIGURE MEASURES, AND THAT IS A DECISION
   * RATHER THAN A MISTAKE."** *It also said:* "If actuals are ever computed for
   * this metric … raise the naming with the owner again — a figure that
   * disagrees with its own label is survivable while nothing computes it, and
   * **stops being survivable the moment something does.**"
   *
   * **That moment arrived.** Actuals are computed for every metric, and on
   * production on 2026-09-11 this one read **£0 while COLLECTED read £96.4K** —
   * because the only accepted quote in the cycle is in EUR. A tile saying
   * nothing had been invoiced, beside a tile saying ninety-six thousand pounds
   * of invoices had been paid, is an impossible pair of statements and it is
   * what the owner reported.
   *
   * 🔴 **AND THE RENAME IS COMPULSORY RATHER THAN TIDY.** A real `invoiced`
   * metric now exists and legitimately owns the word, so leaving this one here
   * would put **one label on two different metrics** — the exact collision the
   * superseded comment was itself warning about. The label moved; the slug did
   * not, because it is the wire contract.
   *
   * ⚠️ **The reading that survives unchanged: `booked` is ACCEPTED QUOTES.** A
   * quote accepted in March and invoiced in April falls in a different period
   * under the two metrics, and neither is a correction of the other.
   *
   * ⚠️ **"Invoiced" still names more than one thing and always will** — this
   * label's new owner `invoiced`, the funnel tile of that name, and a DEAL
   * STAGE, which is the same stage that defines `won_deals`. Do not reconcile
   * them by making the numbers match; they measure different stages of one sale.
   */
  booked: 'Accepted revenue',
  /*
   * ⚠️ **The label that `booked` used to carry, now on the metric that earns
   * it.** Owner's decision 2026-09-11: invoices BILLED in the window, every
   * status except Cancelled. It is NOT settable as a target today — see
   * `TARGET_METRICS` for the probe that measured that.
   */
  invoiced: 'Invoiced revenue',
  collected: 'Collected revenue',
  won_deals: 'Deals won',
  quoted: 'Quoted revenue',
  pipeline: 'Pipeline value',
};

export function targetMetricLabel(metric: TargetMetric): string {
  /*
   * Falls back to the raw slug rather than an em dash: a metric this build does
   * not know about means the contract has moved, and showing the server's own
   * word is what lets somebody recognise that instead of seeing a blank.
   *
   * The annotation is load-bearing — the map is exhaustive over the union, so
   * tsc types the lookup as `string` and would consider the `??` dead. It is not
   * dead at runtime, which is the only place a widened enum arrives.
   */
  const label: string | undefined = TARGET_METRIC_LABELS[metric];
  return label ?? metric;
}

/**
 * The `baseCurrency` to SEND for a metric — the pair rule, in one expression.
 *
 * `null` for a count, whatever is in the picker; the trimmed, upper-cased code
 * for a money metric. Callers must not write `metric` to a payload without also
 * writing this, because the server validates the post-merge state and refuses
 * the contradiction rather than auto-clearing.
 */
export function baseCurrencyFor(metric: TargetMetric, code: string): string | null {
  if (!metricIsMoney(metric)) return null;
  const trimmed = code.trim().toUpperCase();
  return trimmed === '' ? null : trimmed;
}

/**
 * Whether a subject type carries a billing region at all — the single decision
 * point, and the exact counterpart of {@link metricIsMoney}.
 *
 * A **team** target is set per region: *"the account-lead team, in Europe"*.
 * There is deliberately **no overall team target**, so the region is not an
 * optional refinement of one — it is part of what a team commitment IS. A
 * `user` or `organisation` target stays overall and carries no region.
 *
 * ⚠️ **Both directions are a 422 naming `billingRegion`**, checked post-merge
 * server-side: a team target with no region, and a person's or the
 * organisation's target that carries one. So this is not a display rule — it
 * decides what may be SENT, exactly as `metricIsMoney` does for `baseCurrency`.
 *
 * ⚠️ **A region is ORTHOGONAL to currency and neither rule may be derived from
 * the other.** `metricIsMoney` is untouched by any of this: `won_deals` is a
 * count in every region, and a team target on `won_deals` still names one.
 */
export function subjectTypeCarriesBillingRegion(subjectType: TargetSubjectType): boolean {
  return subjectType === 'user_group';
}

/**
 * The `billingRegion` to SEND for a subject type — the pair rule, in one
 * expression, exactly as {@link baseCurrencyFor} is for `{metric,
 * baseCurrency}`.
 *
 * `null` for a `user` or `organisation` target whatever the control holds, and
 * the chosen band for a team one. Callers must not write `subjectType` to a
 * payload without also writing this: the server validates the post-merge state
 * and refuses the contradiction rather than auto-clearing, so a lone
 * `subjectType` is a 422 about a field nobody touched.
 *
 * ⛔ **NOTHING IS INVENTED WHEN A TEAM FORM HAS NO REGION CHOSEN — it returns
 * `null` and lets the server refuse.** Defaulting to `'none'` there would be
 * the most damaging line available in this feature: `'none'` is a REAL
 * population (the companies nobody has banded), so an unanswered control would
 * silently commit a number against it, save cleanly, and read back as a
 * deliberate choice. **An empty control is not a value.** The refusal is the
 * design.
 *
 * ⚠️ **It takes the region as `TargetBillingRegion | null`, not a string, so a
 * picker's empty option cannot arrive spelled `''`.** The four bands and
 * `'none'` are the whole vocabulary and it lives in
 * `src/lib/billing-region.ts`; this module deliberately holds no second copy of
 * the slugs, the labels or the ordering — **import them from there.**
 */
export function billingRegionFor(
  subjectType: TargetSubjectType,
  region: TargetBillingRegion | null,
): TargetBillingRegion | null {
  return subjectTypeCarriesBillingRegion(subjectType) ? region : null;
}

/**
 * 🔴 **THE PLURAL SIBLING — 2026-09-15, and the one a form should now call.**
 *
 * A target may name SEVERAL bands since that day (`"UK + EU"` is
 * `united_kingdom` AND `europe`), so this decides what goes in `billingRegions`
 * exactly as {@link billingRegionFor} decides the singular key.
 *
 * ⛔ **THE TWO MUST NEVER BOTH BE SENT.** The server refuses a body carrying
 * both with a **422 naming both** — deliberately, with no precedence rule,
 * because an ambiguous instruction must fail loudly rather than be resolved on
 * the caller's behalf. A form sends ONE of them; this is the one to send.
 *
 * ⚠️ **`null` IS NOT-REGION-SCOPED AND `[]` IS NOT THE SAME THING.** On a
 * `user` or `organisation` target the answer is `null` — those rows may carry no
 * band at all.
 *
 * ⛔ **AN EMPTY SET ALSO ANSWERS `null`, AND THAT IS THE MOST IMPORTANT LINE IN
 * THIS FUNCTION.** On a `user_group` target an empty set is a **refusal**, not a
 * clear — the server's CHECK requires a non-empty band there — so this hands the
 * server a `null` it will refuse rather than letting the client resolve an
 * unanswered control.
 *
 * ⛔ **NOTHING IS INVENTED FROM AN EMPTY SET. Defaulting to `['none']` would be
 * the single most damaging line available in this feature**: `'none'` is a REAL
 * population — the companies with no country of record — so an untouched control
 * would silently commit a number against them, save cleanly with a `200`, and
 * read back for ever as a deliberate choice. **An empty control is not a value.**
 *
 * ⚠️ **THAT GUARD ARRIVED FROM TWO LOCAL COPIES AND WAS CARRIED IN RATHER THAN
 * LOST.** `GroupTargetsCard` and `UserTargetsModal` each held their own version
 * of this while this module was being written; the copies are gone and the rule
 * they held is here. It is the `financial-shared.ts` precedent — when a
 * duplicate is retired, the STRONGER of the two behaviours is what survives.
 *
 * ⚠️ **ORDER IS NOT MEANINGFUL AND MUST NOT BE MADE SO HERE.** The server
 * canonicalises by sorting, because that is what makes its unique key still mean
 * *the same set*. Sorting a second time in the client would be a second copy of
 * a rule the server owns — right today, and silently wrong the first time the
 * canonical order changes. ⛔ Compare two sets by CONTENT, order-independently.
 *
 * ⚠️ **A COPY IS RETURNED, never the caller's array.** A form's draft state is
 * live; handing the payload builder the same reference means a later tick edits
 * a body already on its way.
 */
export function billingRegionsFor(
  subjectType: TargetSubjectType,
  regions: readonly TargetBillingRegion[] | null,
): TargetBillingRegion[] | null {
  if (!subjectTypeCarriesBillingRegion(subjectType)) return null;
  if (regions === null || regions.length === 0) return null;
  return [...regions];
}

/* -------------------------------------------------------------------------
 * Group targets, and what can be attributed to a group.
 * --------------------------------------------------------------------- */

/**
 * WHICH RELATIONSHIP a group's figure was computed through — the label only.
 *
 * ⚠️ **The DECISION is the server's and is never re-derived here.** Which groups
 * attribute is decided by which columns the revenue documents carry
 * (`account_lead_id` on five tables, `client_success_manager_id` on two), which
 * is a schema fact this app cannot see. `TargetActuals.attribution` carries the
 * answer, and this map only puts English on the `via` it names. **Never map a
 * group SLUG to a relationship here** — that would be a second copy of
 * `RELATIONSHIP_BY_GROUP_SLUG` in a place nothing keeps in step, which is the
 * failure this codebase has shipped three times on admin-editable masters.
 *
 * The two are genuinely different questions of the data and the reader comparing
 * one figure with the other needs to know which they are looking at: the account
 * lead resolves the override on the document falling back to its company, while
 * the client success manager reaches through the company ALONE, because no
 * revenue document carries a CSM column at all.
 */
export const ATTRIBUTION_VIA_LABELS: Record<'account_lead' | 'client_success_manager', string> = {
  account_lead: 'the account lead on each record',
  client_success_manager: 'the client success manager on each company',
};

export function attributionViaLabel(via: 'account_lead' | 'client_success_manager'): string {
  /*
   * Falls back to the raw slug for the same reason `targetMetricLabel` does, and
   * the annotation is load-bearing for the same reason: the map is exhaustive
   * over the union, so tsc would otherwise consider the `??` dead. It is not
   * dead at runtime, which is the only place a widened union arrives.
   */
  const label: string | undefined = ATTRIBUTION_VIA_LABELS[via];
  return label ?? via;
}

/**
 * A stored `targetAmount` as display text.
 *
 * ⚠️ **Never `Number()`.** It arrives as an exact decimal string even for the
 * count (`"12.00"`), and `formatDecimalString` groups it as text without ever
 * touching a float.
 *
 * A money target reads `250,000.00 GBP` — the code always, because there is no
 * conversion anywhere in this system and a bare figure across two currencies is
 * a comparison nobody can make. A count sheds an exact `.00` tail, since
 * "12.00 deals" reads as a rounding artefact; a genuinely fractional count is
 * shown in full rather than hidden, because it would be a real fact about the
 * row and worth someone seeing.
/*
 * The LONG form of a figure — `250,000.00 GBP`, `12` — moved down to
 * `formatFigureAmount`, which is where its doc comment now lives. It sat here,
 * detached from the function it described and directly above the block below,
 * which is how the two rules came to be read as one.
 */

/**
 * A figure as a HEADLINE, split so the unit suffix can be styled apart from
 * the number.
 *
 * ⚠️ **THE RULE, IN ONE PLACE, BECAUSE TWO DEVELOPERS FORMATTING MONEY TWO WAYS
 * IS HOW ONE SCREEN DISAGREES WITH ANOTHER.** Every figure on the funnel panel
 * is formatted by this function and by nothing else:
 *
 *     a count             grouped in full, no symbol, no unit     40, 1,200
 *     money < 1,000       symbol, TWO decimals                    £0.00, £813.00
 *     money >= 1,000      symbol, compact, TWO decimals           £20.00K, £1.23M
 *
 * 🔴 **TWO DECIMALS ON EVERY MONEY FIGURE — owner's decision, 2026-09-13**,
 * across the whole Management Dashboard.
 *
 * *SUPERSEDED: "money < 1,000 → symbol, WHOLE units, `£0`, `£813`; money >=
 * 1,000 → symbol, compact, at most one decimal, `£20K`, `£1.2M`. **No pence
 * anywhere, and no compaction below a thousand.** Both halves are the fix for
 * one inconsistency: the panel used to render `£20k` beside `£0.00`, so two
 * tiles showed two different kinds of number and the eye read them as measuring
 * different things. Dropping the pence is what makes the set agree."*
 *
 * ⚠️ **THE SUPERSEDED RULE'S GOAL SURVIVES AND ONLY ITS MECHANISM INVERTED —
 * WHICH IS THE WHOLE REASON THE SUB-1,000 BRANCH MOVED TOO, THOUGH NOBODY ASKED
 * IT TO.** What that paragraph was protecting was *the set agreeing*: one kind
 * of number across the panel, so the eye does not read two tiles as measuring
 * two different things. It achieved that by dropping pence everywhere. With the
 * owner putting pence back on the COMPACTED figures, leaving the sub-1,000
 * branch at whole units would have reintroduced the exact defect that paragraph
 * exists to prevent, merely the other way round — `£813` beside `£20.00K` is two
 * kinds of number again. So both branches carry exactly two decimals and the set
 * agrees again. **Obeying the old rule's WORDS ("no pence") would have defeated
 * its PURPOSE**, and the purpose is written down one line above the words.
 *
 * ⚠️ **`£0.00` IS NO LONGER THE UGLY CASE IT IS CITED AS ABOVE.** The superseded
 * text names it as half of the original mismatch, but it was only ever
 * mismatched BESIDE `£20k`. Beside `£20.00K` it agrees, which is the point —
 * so it is not evidence for keeping whole units below a thousand.
 *
 * The threshold stays at a thousand, and two decimals make that case stronger
 * rather than weaker: `£813` compacted is `£0.81K`, which is longer than the
 * number it abbreviates AND lossy about a figure small enough to state exactly.
 *
 * ⚠️ **Display only. Never parse this back, and never compare two of them.**
 * `1.23M` has thrown away everything below the ten thousand, so two figures
 * differing by nearly 10,000 render identically. Anything doing arithmetic reads
 * the exact decimal string. *SUPERSEDED with the 2026-09-13 ruling: "`1.2M` has
 * thrown away everything below the hundred thousand, so two figures differing by
 * 40,000 render identically."* **The second decimal shrank the blind spot by a
 * factor of ten; it did not remove it**, and a warning that quietly becomes
 * wrong about its own magnitude is the shape this file keeps recording.
 *
 * ⚠️ **Returned as THREE pieces, not one string, and the split is the point.**
 * The unit is painted in the gold accent and the digits are not, so a caller
 * needs the boundary. It is found with `formatToParts` and the part typed
 * `compact` — never by slicing the last character, which is wrong the moment a
 * locale or a currency puts a symbol after the number, and never by casing the
 * whole string, which would upper-case a currency code too.
 *
 * ⚠️ **Render the three back to back with no whitespace between them.** The
 * DOM text has to stay exactly `£20.00K` so it is announced as one figure and
 * copies as one figure; an inline `<span>` around `unit` does not break either,
 * but a line break, a `display: block`, or an `aria-hidden` on any piece does.
 *
 * ⚠️ **A MONEY FIGURE WITH NO CURRENCY CODE IS TWO DIFFERENT FACTS, AND `role`
 * IS WHICH ONE.** *SUPERSEDED: "A money metric with no currency keeps the long
 * form's complaint rather than printing a bare compact number: a figure that
 * cannot be compared should not be the most confident thing on the panel."*
 *
 * That sentence was written about a TARGET and then applied to both, so an
 * ACTUAL of zero — the ordinary state of a metric nobody has set a target for —
 * rendered `0.00 (no currency recorded)` at `text-3xl` on every tile, reporting
 * a defect that does not exist. The split is in `formatFigureAmount`, and the
 * reasoning is on `FigureRole`.
 *
 * 🔴 **AND `role` DECIDES NOTHING IN THIS FUNCTION ANY MORE — corrected
 * 2026-09-13, noticed while moving the decimals.** *SUPERSEDED: "`role:
 * 'target'` keeps the complaint, unchanged and load-bearing. `role: 'actual'`
 * compacts the digits with **no symbol** — `0`, `813`, `20K` — because the
 * currency is unstated for a reason the panel states in words right beside the
 * figure, and the largest text on the screen is not where to repeat it."*
 *
 * The owner's 2026-09-02 GBP-only decision gave **both** roles a symbol, and the
 * live reasoning for that sits at the `code` fallback in the function body. This
 * paragraph was left behind by it and went on describing a symbol-less `actual`
 * that the code had already stopped producing — **a doc comment contradicting
 * the function directly beneath it, which is the artefact a careful reader
 * consults INSTEAD of re-deriving.** It is not re-explained here: one copy of
 * that reasoning, in the body, beside the line it governs.
 */
export interface FigureParts {
  /** Everything before the unit — symbol, digits, separators. */
  head: string;
  /** `K`, `M`, or `''`. Upper-cased here; **never non-empty for a count.** */
  unit: string;
  /** Everything after the unit. Empty under `en-GB`, which leads with the symbol. */
  tail: string;
}

/** The whole figure as one string — for a `title`, a label, or a test. */
export function figureText(parts: FigureParts): string {
  return `${parts.head}${parts.unit}${parts.tail}`;
}

/** A figure with no unit to split off — the shape every fallback path returns. */
function bare(head: string): FigureParts {
  return { head, unit: '', tail: '' };
}

/**
 * THE ONE COMPACT-MONEY IMPLEMENTATION. Every money figure on every dashboard
 * is rendered by this function and by nothing else.
 *
 * ⚠️ **PRIVATE, AND IT MUST STAY THAT WAY: it takes a `number` and a code it
 * TRUSTS.** Both public wrappers do real work before reaching it — deciding
 * whether the metric is money at all, resolving a blank code, and turning an
 * exact decimal string into a float only once it is known to be finite. A
 * caller reaching this directly would skip all three.
 *
 * ⚠️ **`code` MUST BE NON-EMPTY.** `style: 'currency'` with an empty `currency`
 * is a `RangeError`, and this is a render path — an uncaught throw here does not
 * blank a figure, it unmounts the React tree and blanks the page. Both wrappers
 * resolve the fallback before calling, which is why the guard lives at their end
 * and not at this one.
 *
 * ⚠️ **EXTRACTED 2026-09-13 RATHER THAN COPIED.** The three revenue panels
 * needed compact money without a target metric, and a second `Intl.NumberFormat`
 * call would have agreed with this one the day it was written and drifted on the
 * first correction — and what drifts is how money reads on one dashboard versus
 * another, which nothing typechecks and nobody reports. One decimal place moved
 * today; the next change must not have two places to move it in.
 */
function compactCurrencyParts(value: number, code: string): FigureParts {
  const compact = value >= 1000;
  /*
   * `Intl` supplies the symbol, so GBP renders as £ and INR as ₹ rather than
   * this file carrying a table of them that goes stale the first time Xero
   * enables a currency nobody here has heard of.
   *
   * ⚠️ **BOTH BOUNDS, BOTH 2, ON BOTH BRANCHES — owner's 2026-09-13 decision,
   * and the `minimum` half is as load-bearing as the `maximum` one.** With a
   * minimum of 0 a round twenty thousand renders `£20K` while its neighbour
   * renders `£20.55K`, which is the ragged set the whole rule exists to prevent;
   * the owner asked for two decimals, not for up to two.
   *
   * ⚠️ Explicit fraction digits also override the compact default, which is
   * `maximumSignificantDigits: 2` — leave them out and `1,234,567` comes back
   * `£1.2M` whatever the maximum says.
   *
   * ⚠️ Forcing 2 is right for every currency EMERGE has active (GBP, EUR, USD
   * are all two-minor-unit). A zero-decimal currency such as JPY would render
   * `¥1,000.00`; this is the line to revisit if one is ever enabled, rather than
   * a reason to drop back to the currency's own default and let the panel go
   * ragged again.
   */
  const parts = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: code,
    notation: compact ? 'compact' : 'standard',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).formatToParts(value);

  const at = parts.findIndex((part) => part.type === 'compact');
  if (at === -1) return bare(parts.map((part) => part.value).join(''));

  return {
    head: parts.slice(0, at).map((part) => part.value).join(''),
    /*
     * Upper-cased rather than hardcoded: ICU spells this `k` in some versions
     * and `K` in others, and a hardcoded table would also have to know every
     * unit `en-GB` can emit.
     *
     * ⚠️ **DO NOT DELETE THIS AS DEAD, AND NOTHING RUNNING HERE CAN PROVE IT IS
     * NOT.** Measured 2026-08-30: Node 22 / ICU 77 already returns `K`, so a
     * check run under Node passes identically with the call removed — a
     * mutation that cannot go red. The browser this panel was measured in
     * returns `k` (the panel rendered `£20k`, which is the defect being fixed),
     * so the call is load-bearing exactly where it cannot be tested. Removing
     * it puts a lower-case unit back on screen and nothing anywhere fails.
     */
    unit: (parts[at]?.value ?? '').toUpperCase(),
    tail: parts.slice(at + 1).map((part) => part.value).join(''),
  };
}

export function compactFigureParts(
  metric: TargetMetric,
  amount: string,
  currency: string | null,
  role: FigureRole,
): FigureParts {
  const value = Number(amount);
  if (!Number.isFinite(value)) return bare(formatFigureAmount(metric, amount, currency, role));

  /*
   * A count is never compacted and never carries a unit, so it goes through the
   * long form unchanged: `formatDecimalString` groups it as text, and the
   * `.00` tail is shed there. That also keeps a genuinely fractional count
   * visible rather than rounding it into a whole one — it would be a real fact
   * about the row.
   */
  if (!metricIsMoney(metric)) return bare(formatFigureAmount(metric, amount, currency, role));

  /*
   * ⚠️ **A money figure with no code is denominated in `DASHBOARD_CURRENCY`,
   * so the headline carries a `£` like every one of its neighbours.** Owner's
   * 2026-09-02 GBP-only decision. *SUPERSEDED: a `target` with no code dropped
   * to the long form so its `(no currency recorded)` complaint survived, and an
   * `actual` compacted with no symbol at all.* Neither branch can be right on a
   * dashboard that is GBP by construction: the first reported a defect that
   * cannot exist, and the second put a symbol-less number beside `£20K`, where
   * the eye reads the two as measuring different things. `role` is retained in
   * the signature — it is a shipped export other files are mid-edit against.
   */
  const raw = currency?.trim() ?? '';
  const code = raw === '' ? DASHBOARD_CURRENCY : raw;

  return compactCurrencyParts(value, code);
}

/**
 * COMPACT MONEY THAT IS NOT TIED TO A TARGET METRIC — `£20.00K`, `£1.23M`,
 * split so the unit can be styled apart from the digits.
 *
 * For the Management Dashboard panels that show money without a metric behind it
 * — Revenue by Company, Revenue by Product, Top Movers — so they read exactly
 * like the funnel tiles beside them. It shares its whole implementation with
 * `compactFigureParts` (`compactCurrencyParts`), which is the point of it
 * existing at all: those panels rendered full grouped decimals through
 * `formatDecimalString` while the tiles compacted, so one dashboard showed two
 * kinds of number.
 *
 * ⚠️ **`null` IS AN EM DASH AND NEVER A ZERO, and `'0.00'` IS A REAL ZERO.** The
 * distinction is stated all over this file and it is the whole reason the
 * parameter is nullable: *nobody has a figure here* and *the figure is nothing*
 * are different facts, and a `?? 0` upstream collapses them into a confident
 * claim that a company billed nothing. The em dash comes from
 * `formatDecimalString(null)` rather than a literal, so this file does not own a
 * second copy of that character.
 *
 * ⛔ **THE CURRENCY IS REQUIRED AND IS DELIBERATELY NOT DEFAULTED — there is no
 * `currency = DASHBOARD_CURRENCY` on this signature and one must not be added.**
 * These panels always know theirs, so a default would only ever fire where a
 * caller had lost track of it, and it would fire SILENTLY, relabelling a foreign
 * figure as GBP. **A bare number is forbidden for the same reason**: nothing in
 * EMERGE converts a foreign amount today, so a figure without its unit is one
 * somebody will add to the figure beside it — which is arithmetic across two
 * currencies, producing a total that is wrong and looks fine. The unit is what
 * makes the sum visibly refuse.
 *
 * ⚠️ **Display only — never parse it back and never compare two.** See
 * `FigureParts` for what the second decimal does and does not recover.
 */
export function compactMoneyParts(amount: string | null, currency: string): FigureParts {
  /*
   * A blank string is folded in with `null`: both mean *there is no figure*, and
   * letting `''` through would print a lone em dash with a currency code stapled
   * to it (`— GBP`), which reads as a figure that failed rather than one nobody
   * has.
   */
  const text = amount?.trim() ?? '';
  if (text === '') return bare(formatDecimalString(null));

  /*
   * ⚠️ **A CRASH GUARD, NOT A LICENCE TO OMIT THE ARGUMENT.** The signature
   * requires a currency and the doc above says why; this catches the `''` that
   * only a caller defect can produce, because the alternative is a `RangeError`
   * thrown inside a render, which React escalates to unmounting the tree — a
   * blank page because one figure had no code. Resolving to `DASHBOARD_CURRENCY`
   * matches what that constant is already documented as being for: the fallback
   * for a money figure the wire left empty.
   */
  const trimmedCode = currency.trim();
  const code = trimmedCode === '' ? DASHBOARD_CURRENCY : trimmedCode;

  const value = Number(text);
  /*
   * Not a number we can compact — so show what the server actually sent, in the
   * long form, rather than swallowing it into an em dash. `formatDecimalString`
   * groups digits as text and copies anything else through verbatim, which is
   * what makes an unexpected value legible instead of invisible.
   */
  if (!Number.isFinite(value)) return bare(`${formatDecimalString(text)} ${code}`);

  return compactCurrencyParts(value, code);
}

/**
 * `compactMoneyParts` as one string — for a `title`, a label, or a test.
 *
 * ⚠️ **The `title` is where the EXACT figure belongs, and this is not it.** This
 * is the same compacted, lossy text the cell shows, joined; a panel wanting the
 * unrounded amount on hover reads the decimal string through
 * `formatDecimalString` instead. Use this where a string is needed for an
 * `aria-label` or a non-styled label — somewhere the head/unit split cannot be
 * rendered — not to reassure a reader about precision it does not carry.
 */
export function formatCompactMoney(amount: string | null, currency: string): string {
  return figureText(compactMoneyParts(amount, currency));
}

/**
 * WHICH FIGURE is being formatted — because a missing currency code means
 * opposite things on the two of them, and one rule for both is what put a
 * defect notice on an ordinary empty dashboard.
 *
 * ⚠️ **A money TARGET with no currency is a BROKEN ROW.** Somebody committed to
 * a number and the row cannot say what it is denominated in. The server refuses
 * that pair on a write, so a stored one is a real defect and saying so beats
 * printing a figure that looks complete and is not comparable.
 *
 * ⚠️ **A money ACTUAL with no currency is the ORDINARY EMPTY STATE.** An
 * actual's currency mirrors the target it is measured against, so *no target
 * means no currency* — there is nothing broken to report, and reporting one
 * puts `(no currency recorded)` on every tile of a dashboard whose only fault
 * is that nobody has set targets on it yet. That is what the owner was shown on
 * 2026-08-31, in the largest text on the page, wrapped over three lines.
 *
 * ⚠️ **The absent currency is still EXPLAINED, just not here.** Every route to
 * it runs through a null target, and the panel words that once — per tile as
 * "No target set", or once for the whole panel when every metric is unset. If a
 * caller ever renders an actual without that explanation nearby, this decision
 * needs re-taking rather than working around.
 */
export type FigureRole = 'target' | 'actual';

/**
 * A stored decimal as display text, read as `role`.
 *
 * ⚠️ **Never `Number()`.** It arrives as an exact decimal string even for the
 * count (`"12.00"`), and `formatDecimalString` groups it as text without ever
 * touching a float.
 */
export function formatFigureAmount(
  metric: TargetMetric,
  amount: string,
  currency: string | null,
  /*
   * ⚠️ **Underscored because it now decides NOTHING here, and KEPT because the
   * signature is shipped.** Under the GBP-only rule both roles format
   * identically; `_` is what keeps `noUnusedParameters` quiet without removing
   * a positional parameter three other files pass. See `FigureRole`.
   */
  _role: FigureRole,
): string {
  const figure = formatDecimalString(amount);
  /*
   * A count sheds an exact `.00` tail, since "12.00 deals" reads as a rounding
   * artefact; a genuinely fractional count is shown in full rather than hidden,
   * because it would be a real fact about the row.
   */
  if (!metricIsMoney(metric)) return figure.endsWith('.00') ? figure.slice(0, -3) : figure;

  /*
   * The code always, because there is no conversion anywhere in this system and
   * a bare figure across two currencies is a comparison nobody can make.
   *
   * ⚠️ **A money figure with NO code falls back to `DASHBOARD_CURRENCY` rather
   * than printing a bare number or a complaint — owner's 2026-09-02 GBP-only
   * decision.** *SUPERSEDED: a `target` role printed `(no currency recorded)`
   * and an `actual` printed the bare figure.* On a dashboard where every figure
   * is GBP by construction, "no currency recorded" reported a defect that
   * cannot exist, and a bare number beside a `£`-marked neighbour read as a
   * different kind of quantity. `role` is left in the signature and still
   * decides nothing here — it is threaded through `compactFigureParts` and
   * removing it would be a public-signature change other files are mid-edit
   * against.
   */
  const code = currency?.trim() ?? '';
  return `${figure} ${code === '' ? DASHBOARD_CURRENCY : code}`;
}

/** The commitment. A money row with no currency reads as `DASHBOARD_CURRENCY`. */
export function formatTargetAmount(metric: TargetMetric, amount: string, currency: string | null): string {
  return formatFigureAmount(metric, amount, currency, 'target');
}

/** What actually happened. A money row with no currency reads as `DASHBOARD_CURRENCY`. */
export function formatActualAmount(metric: TargetMetric, amount: string, currency: string | null): string {
  return formatFigureAmount(metric, amount, currency, 'actual');
}

/**
 * What a figure LEFT OUT, worded once.
 *
 * `null` below one, which is the whole guard: a tile renders this or renders
 * nothing, and no caller writes its own `if`. **`0` must print NOTHING.** An
 * "0 excluded" line on all five tiles, on every window, is the noise that
 * teaches somebody to stop reading the small print under a figure — at which
 * point the one time it says 2 is the one time it is not read.
 *
 * ⚠️ **NO CURRENCY IS NAMED, AND THE REASON HAD TO BE RE-DERIVED RATHER THAN
 * INHERITED.** *SUPERSEDED: "The exclusion is taken against the server's own
 * base-currency constant … Nothing on the wire states the currency the sum was
 * taken in, so 'in another currency' is the strongest true statement
 * available."* The currency the sum was taken in **is** on the wire now
 * (`measuredInCurrency`), and the caller words its heading from it — but that
 * names the currency converted INTO, where this counts documents that could not
 * be priced at all, which may be several currencies and is a different fact.
 * `row.baseCurrency` is a third thing again: the stored TARGET's currency,
 * wrong in exactly the case the field exists to describe. The full reasoning is
 * on `TargetActual.excludedByCurrency`.
 *
 * ⚠️ **It states a FACT, not an instruction, and THAT half survives the meaning
 * change below untouched — only the fact moved.** *SUPERSEDED: "Nobody can
 * convert anything from this screen, and there is no other screen to send them
 * to — nothing in EMERGE converts a foreign amount at all."* Since backend
 * migration 178 `/targets/actuals` **does** convert, so what is left out is a
 * document whose own date has no stored rate. Nothing a reader can press from
 * here changes that — the rate table is fetched elsewhere — so the sentence
 * still says what is true rather than what to do.
 *
 * `detail` is for a `title`, following the rule the figures already keep: the
 * cell carries the short form and the exact statement lives on the element.
 */
export interface ExcludedNote {
  /** Under a figure. Short by policy — a tile is a small space. */
  short: string;
  /** The same fact in full, for a `title`. */
  detail: string;
}

export function excludedByCurrencyNote(excluded: number | null): ExcludedNote | null {
  /*
   * 🔴 **THE COUNT CHANGED MEANING WHILE KEEPING ITS NAME — 2026-09-13, backend
   * migration 178. THE WORDING HERE MOVED WITH IT; NOTHING ELSE DID.**
   *
   * *SUPERSEDED, and it was live on production for the whole of 13 Sep:
   * `short` "N in another currency", `detail` "N paid or issued documents in
   * another currency are not counted in this figure. Switch the currency above
   * to see them."*
   *
   * `/targets/actuals` **converts** a foreign document now, at the rate for its
   * own date. So `excludedByCurrency` no longer counts documents in another
   * currency — it counts documents **whose date has no stored rate**, which the
   * backend's own rule 3 states in terms: *"A document whose rate CANNOT BE
   * FOUND is still excluded and still counted — `excludedByCurrency` survives
   * with its meaning moved."*
   *
   * ⛔ **AND THE OLD SENTENCE WAS WORSE THAN MERELY STALE, WHICH IS WHY IT IS
   * QUOTED ABOVE RATHER THAN DELETED.** *"In another currency"* named a POLICY
   * the reader could not change and had no reason to question. The truth is
   * *"we could not price these"* — a gap in a rate table that a backfill
   * closes — so the old wording sent somebody to accept a figure that was
   * short for a fixable reason. **It was wrong in the reassuring direction**,
   * which is the direction nobody reports.
   *
   * ⛔ **AND ITS CLOSING CLAUSE WAS AN INSTRUCTION THAT NO LONGER WORKS.**
   * *"Switch the currency above to see them"* was followable while the count
   * meant a filtered-out currency. A document with no rate for its date is
   * missing from **every** currency the switcher offers except the one it was
   * raised in, so following it now mostly changes the figure without revealing
   * the documents. **A dead instruction is worse than no instruction**: it
   * reads as a way out and spends the reader's trust when it does not work.
   *
   * ⚠️ **`Number.isFinite` IS CARRIED IN FROM THE DUPLICATE THIS REPLACES**, and
   * it is a real guard rather than defensiveness: `NaN <= 0` is `false`, so a
   * `NaN` reaching the old test rendered **"NaN in another currency"** under a
   * money figure. `financial-shared.ts` held a stricter local copy for exactly
   * this and its own note said to carry the guard in rather than lose it.
   *
   * ── what did NOT change ───────────────────────────────────────────────────
   *
   * ✅ **RESTORED 2026-09-11. THE GBP-ONLY RULING THAT NEUTERED THIS IS
   * WITHDRAWN.**
   *
   * *SUPERSEDED: "ALWAYS `null` SINCE 2026-09-02 — the dashboards are GBP-ONLY
   * on the owner's instruction, so this note has nothing to tell a reader …
   * every dashboard figure is `DASHBOARD_CURRENCY`, so 'excluded because it is
   * in another currency' describes a distinction the page no longer draws."*
   *
   * The page draws it again: the Management Dashboard has a currency switcher,
   * and `/targets/actuals` takes a `baseCurrency`. **This note is what stops
   * the switcher being set to the wrong currency in silence** — measured on
   * production 2026-09-11, asking in GBP returned `excludedByCurrency: 234`
   * against 652 USD and 89 EUR invoices, and the figure looked like a quiet
   * quarter rather than the wrong filter.
   *
   * ⚠️ **Its own note predicted this: "if multi-currency dashboards ever come
   * back, this function is the one place that has to change".** It was, and
   * it did — which is the whole return on the signature having been left alone.
   *
   * ⚠️ **`0` AND `null` BOTH RENDER NOTHING, FOR DIFFERENT REASONS.** `0` is a
   * measurement — nothing was excluded, so there is nothing to say. `null` is
   * *the server did not compute one*, and inventing a sentence from an absence
   * would be a claim nobody made. Neither is a case for small print.
   *
   * ⚠️ **IT STATES A FACT, NOT AN INSTRUCTION**, and that half of the original
   * reasoning survives untouched: nothing in EMERGE converts a foreign amount,
   * so "N in other currencies" is the whole actionable content. What a reader
   * DOES about it is switch the currency, which is a control they can already
   * see — the note does not need to tell them to.
   *
   * ⚠️ **The currency is deliberately NOT named here.** This module cannot know
   * which one the figure was measured in without being told, and naming
   * `row.baseCurrency` would be the stored TARGET's currency, which is a
   * different fact. The caller has `measuredInCurrency` and words the heading;
   * this says only how many were left out.
   */
  if (excluded === null || !Number.isFinite(excluded) || excluded < 1) return null;
  const noun = excluded === 1 ? 'document' : 'documents';
  const dates = excluded === 1 ? 'its own date' : 'their own dates';
  const them = excluded === 1 ? 'it' : 'them';
  return {
    short: `${excluded} could not be converted`,
    detail:
      `${excluded} ${noun} had no exchange rate stored for ${dates}, so this figure ` +
      `leaves ${them} out. The rate is missing, not the revenue.`,
  };
}

/**
 * HOW A CONVERTED FIGURE WAS ARRIVED AT — worded ONCE, for every surface that
 * renders `/targets/actuals`.
 *
 * Owner's instruction, 2026-09-13: *"add a currency conversion disclaimer that
 * the values might be approximated."*
 *
 * `/targets/actuals` converts a foreign document into the requested currency
 * from `f_exchange_rates` (backend migration 178), fed by SEED's own currency
 * API — **a published daily reference rate per calendar day**. Each document is
 * converted at the rate for ITS OWN date: a quote at its issue date, an invoice
 * at its bill date, a paid invoice at its payment date, a deal at its creation
 * date. A published reference rate is not the rate any particular payment
 * settled at — a bank applies its own rate and spread — so a converted total is
 * a fair restatement and not the figure the accounts will show.
 *
 * ⛔ **ONE COPY, AND A SECOND WOULD BE WORSE THAN NONE.** This is a sentence
 * about how MONEY WAS DERIVED, and two copies agree the day they are written
 * and drift on the first correction — the rule this module already keeps for
 * `metricIsMoney` and `src/lib/inclusions.ts` keeps for its wording. Every
 * surface rendering a converted figure imports this; nobody writes their own.
 *
 * ⛔ **IT DESCRIBES CONVERSION, NOT EXCLUSION, AND THE TWO MUST NEVER SHARE A
 * SENTENCE.** A document whose date has NO stored rate is left out entirely and
 * counted — that is `excludedByCurrencyNote` immediately above, which is a
 * different fact reported separately. *Converted, and therefore approximate*
 * and *not counted at all* send a reader to opposite conclusions about the same
 * number; conflating them would tell somebody a figure is short when it is
 * merely rounded, or rounded when it is short.
 *
 * ⛔ **IT MAY ONLY BE MOUNTED WHERE SOMETHING WAS ACTUALLY CONVERTED.** Today
 * that is the Financial Analytics tab and the University Partnerships
 * dashboard, both of which render `/targets/actuals`, **and all three revenue
 * tabs on the Management Dashboard** — Revenue by Product since 2026-09-14,
 * Revenue by Company and Top Movers since 2026-09-18.
 *
 * 🔴 **THE FORBIDDEN LIST IS NOW EMPTY, AND IT EMPTIED ONE MEMBER AT A TIME.**
 *
 * *SUPERSEDED, 2026-09-14, and it was exactly right while all three tabs
 * filtered: "**NOT on Revenue by Company, Revenue by Product or Top Movers**:
 * `src/lib/use-revenue-analytics.ts` FILTERS to one chosen currency … and
 * converts nothing, so this sentence there would be a false statement about how
 * those figures were produced."*
 *
 * *SUPERSEDED IN TURN, 2026-09-18, and it was right for four days: "⛔ **REVENUE
 * BY COMPANY AND TOP MOVERS ARE STILL FORBIDDEN, and the reason is unchanged** —
 * they still filter to one chosen currency and convert nothing. **Do not read
 * the reversal as retiring the list; the list lost one member.**"*
 *
 * **`GET /revenue-tracker` takes a `baseCurrency` too now**, on the owner's
 * instruction, and prices each paid line at its own invoice's `bill_date` — so
 * on a CONVERTED response from either of those two tabs this sentence is the
 * true one and its absence would be the omission.
 *
 * ⚠️ **READ THAT PAIR AS A WARNING ABOUT THE SHAPE OF THIS RULE RATHER THAN AS
 * TWO SPENT SENTENCES.** Both were correct measurements of what the surfaces did
 * on the day, both were written as though they described the surfaces
 * permanently, and both were falsified by somebody converting a tab in another
 * folder. **The list is a reading of which surfaces convert, never a policy about
 * which ones may** — so the question to ask of a new caller is not *is it on the
 * list* but *does its server echo a currency back*. That is what the gate below
 * tests, and it is the only test that cannot go stale.
 *
 * ⛔ **THE GOOGLE SHEET MIRRORS STILL FILTER AND MUST NOT CARRY THIS.** They are
 * the backend's own surfaces and nothing here renders them; converting one is
 * that repo's decision.
 *
 * ⚠️ **AND THE MOUNT IS GATED ON THE SERVER'S ECHO, NOT ON THE PICKER.** A
 * server predating the key ignores `baseCurrency` and answers an ordinary `200`
 * of UNCONVERTED rows, so a tab that mounted this whenever a currency was
 * *chosen* would make the false statement on exactly the deploy where it is
 * least expected. Gate on `measuredInCurrency` coming back.
 *
 * ⚠️ **NO CURRENCY IS NAMED**, for the reason `excludedByCurrencyNote` already
 * gives one function up: the dashboards are switchable, the caller holds
 * `measuredInCurrency`, and this module cannot know it. The caller supplies the
 * unit if its heading needs one.
 *
 * ⚠️ **THIS IS NOT HELPER TEXT, AND A SWEEP MUST NOT TAKE IT.** `CLAUDE.md`
 * carries a standing ⛔ prohibition on helper text — no `hint`, no
 * `description`, no explanatory sentence under a control. This survives under
 * the documented exception: **it states a FACT ABOUT THE DATA that the reader
 * cannot otherwise see.** Nothing on screen distinguishes a figure summed from
 * native amounts from one restated through a reference rate, and the wrong
 * conclusion it prevents — *"this total is what the accounts will show"* — is
 * one a finance reader would act on. It is the same class as the kanban board's
 * **"Indicative — mixed GBP, USD"** on a column total and the revenue tracker's
 * **"Mixed currencies"** on a band, both of which qualify a number rather than
 * instruct anybody. It is attached to a FIGURE, not to a control; there is no
 * control here to fix instead. **Before deleting it, ask what wrong conclusion
 * it prevents** — the answer is not "none".
 *
 * `short` renders; `detail` goes on the element's `title`, following the rule
 * the tiles already keep for their exact decimals and `ExcludedNote` keeps for
 * its own full statement — the dense surface carries the short form and the
 * complete one lives on the element.
 */
export const CONVERSION_APPROXIMATE_NOTE = {
  /** Under the figures. One small line by policy — it qualifies them, it is not about them. */
  short: 'Other currencies are converted at each document’s own date, so totals are approximate.',
  /** The same fact in full, for a `title`. */
  detail:
    'Amounts originally in other currencies are converted using the published daily reference ' +
    'rate for each document’s own date — a quote at its issue date, an invoice at its bill ' +
    'date, a paid invoice at its payment date, a deal at its creation date. A published ' +
    'reference rate is not the rate a bank settled at, so these totals are a fair restatement ' +
    'rather than the figure the accounts will show.',
} as const;

/**
 * The conversion sentence for a `/targets/actuals` answer, by the FX mode the
 * SERVER echoed (owner 2026-10-28).
 *
 * `document` (also an older API, which sends no `fxMode`): the existing
 * `CONVERSION_APPROXIMATE_NOTE.short`. `period_end`: every amount went through
 * GBP and back at one translation rate, as Xero's P&L does, so the sentence
 * names that date and rate. A `period_end` answer with no `translation` echo
 * falls back to the document sentence rather than inventing a rate.
 *
 * ⚠️ The rate is a decimal string and is trimmed to 4 decimals with STRING
 * operations — never `Number`/`parseFloat` (money and rates rule).
 *
 * ⚠️ **Not helper text**: a fact about the data, for the reason given on
 * `CONVERSION_APPROXIMATE_NOTE`. Do not sweep it.
 */
export function conversionNote(input: {
  fxMode: TargetFxMode;
  translation: TargetTranslation | null;
  currency: string;
}): string {
  const { fxMode, translation, currency } = input;
  if (fxMode !== 'period_end' || translation === null) return CONVERSION_APPROXIMATE_NOTE.short;
  return `Converted through GBP at the ${formatDateOnly(translation.date)} rate (1 GBP = ${trimRate(
    translation.rate,
  )} ${currency}), as in Xero’s P&L.`;
}

/** `"1.27345678"` → `"1.2734"`; truncates, never rounds, never parses. */
function trimRate(rate: string): string {
  const trimmed = rate.trim();
  const dot = trimmed.indexOf('.');
  if (dot === -1) return trimmed;
  const fraction = trimmed.slice(dot + 1, dot + 5);
  return fraction === '' ? trimmed.slice(0, dot) : `${trimmed.slice(0, dot)}.${fraction}`;
}

/** `formatTargetAmount` for a whole row, so a caller cannot pair the wrong two fields. */
export function formatTarget(target: Target): string {
  return formatTargetAmount(target.metric, target.targetAmount, target.baseCurrency);
}

/**
 * A window as `Q1 FY26 · 1 Apr 2026 – 30 Jun 2026`.
 *
 * The dates go through `formatDateOnly`, which formats them in UTC without
 * constructing a local `Date` — `new Date('2026-06-30')` is midnight UTC and
 * renders as 29 June for anyone west of Greenwich, which on a period BOUNDARY
 * is the difference between two quarters.
 */
export function describeTargetPeriod(period: TargetPeriod): string {
  const window = `${formatDateOnly(period.startDate)} – ${formatDateOnly(period.endDate)}`;
  return `${period.name} · ${window}`;
}

/* -------------------------------------------------------------------------
 * Windows: a catalogued period, one of its subdivisions, or a typed range.
 * --------------------------------------------------------------------- */

/**
 * The window the funnel panel is reporting on.
 *
 * ⚠️ **`periodId` is `null` for a subdivision and for a typed range, and that
 * is what decides HOW the figures are asked for**: a window carrying an id is
 * fetched by `periodId`, one carrying none by `from`/`to`. Not an optimisation
 * — a quarter of a cycle is catalogued nowhere, so there is no id to send, and
 * a period IS catalogued, so its own id is the exact question rather than a
 * range that happens to coincide with it.
 */
export interface TargetWindow {
  /** Short, for the picker: `F.Y. 2026 - 27`, `Q2`, `Aug 2026`, `Custom range`. */
  name: string;
  /** Inclusive, plain `YYYY-MM-DD`. Compare as STRINGS; never `new Date(...)`. */
  from: string;
  /** Inclusive. */
  to: string;
  /** The catalogued period when this window IS one; `null` otherwise. */
  periodId: number | null;
}

/**
 * A window as `Q2 · 1 Aug 2026 – 31 Oct 2026`.
 *
 * ⚠️ **The dates are half the label and are not decoration.** A subdivision's
 * name alone (`Q2`, `Aug 2026`) does not say which cycle it belongs to, and a
 * period that does not begin on the first of a month makes `May 2026` overstate
 * its own window. Spelling the window out is the only thing stopping a figure
 * being read under the wrong label.
 *
 * Both dates go through `formatDateOnly`, which formats in UTC without
 * constructing a local `Date` — `new Date('2026-06-30')` is midnight UTC and
 * renders as 29 June for anyone west of Greenwich, which on a window BOUNDARY
 * is the difference between two quarters.
 */
export function describeTargetWindow(window: TargetWindow): string {
  /*
   * 🔴 **THE DAY-LEVEL DATES ARE GONE FROM THE ORDINARY CASE — owner's
   * instruction, 2026-09-13, looking at the Month picker.**
   *
   * *SUPERSEDED: `${name} · ${formatDateOnly(from)} – ${formatDateOnly(to)}`,
   * unconditionally — which rendered **"Sept 2026 · Sep 1, 2026 – Sep 30,
   * 2026"**, saying the same thing twice, and overflowed the trigger so the
   * useful half was the half that got truncated.*
   *
   * ⚠️ **THE OLD REASONING WAS NOT WRONG, IT WAS OVER-APPLIED, WHICH IS WHY
   * THIS IS CONDITIONAL RATHER THAN A DELETION.** Its two grounds were that a
   * piece at either end of a period is CLAMPED to it, so a bare name overstates
   * its own window; and that a bare `Q2` never said which cycle it belonged to.
   * **Both still hold — for the cases they actually describe**, and neither
   * describes a whole calendar month, where the name already IS the window:
   *
   *   whole single month    `Sept 2026`            the name is exact
   *   whole several months  `Q2 · Jul – Sep 2026`  `Q2` alone says nothing
   *   CLAMPED either end    `Q1 · Jul 15, 2026 – Sep 30, 2026`
   *
   * ⛔ **THE CLAMPED ARM IS THE ONE THAT MUST NOT BE TIDIED AWAY.** It fires
   * when a period does not begin on the 1st or end on a month end, and there
   * the bare name is a **false statement about a window somebody is reading a
   * figure under** — the piece is short and the label would not say so. It
   * renders on no cycle in production today (all eight run 1 Apr – 31 Mar), so
   * it is invisible until the day somebody catalogues a period that starts
   * mid-month, which is exactly when it matters and exactly when nobody is
   * looking for it.
   *
   * ⚠️ **A third ground the old comment gave is DEAD and is not preserved**:
   * *"it is what the filter box matches on, which is the point of typing
   * `2026-07`"*. The label never contained `2026-07` — `formatDateOnly` renders
   * `Jul 1, 2026` — so that search was never possible and removing the dates
   * takes nothing away from it.
   */
  const from = calendarMonthOf(window.from);
  const to = calendarMonthOf(window.to);
  if (from === null || to === null) return window.name;

  const startsOnFirst = window.from === ymd(from.year, from.month, 1);
  const endsOnLast = window.to === ymd(to.year, to.month, lastDayOfMonth(to.year, to.month));
  if (!startsOnFirst || !endsOnLast) {
    return `${window.name} · ${formatDateOnly(window.from)} – ${formatDateOnly(window.to)}`;
  }

  if (from.year === to.year && from.month === to.month) return window.name;

  const utc = (part: { year: number; month: number }) =>
    new Date(Date.UTC(part.year, part.month - 1, 1));
  const head = from.year === to.year ? MONTH_SHORT.format(utc(from)) : MONTH_LABEL.format(utc(from));
  return `${window.name} · ${head} – ${MONTH_LABEL.format(utc(to))}`;
}

/** The catalogued period itself, as a window. */
export function periodWindow(period: TargetPeriod): TargetWindow {
  return {
    name: period.name,
    from: period.startDate,
    to: period.endDate,
    periodId: period.id,
  };
}

/** `YYYY-MM-DD` from calendar parts. Assembled as text — no `Date` formatting. */
function ymd(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * How many days a calendar month has.
 *
 * ⚠️ **Day 0 of the NEXT month, which the platform resolves to the last day of
 * this one.** February and the 31-day months therefore come out of the calendar
 * rather than out of an assumption: "add 30 days" is wrong in seven months of
 * twelve, wrong differently in a leap year, and its failure is a window that
 * silently misses its own final day.
 *
 * `month` is 1-based, which is why it is passed through unshifted.
 */
function lastDayOfMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Year and 1-based month of a plain `YYYY-MM-DD`, or `null` if it is not one. */
function calendarMonthOf(iso: string): { year: number; month: number } | null {
  const match = /^(\d{4})-(\d{2})-\d{2}$/.exec(iso);
  if (match === null) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isInteger(year) || month < 1 || month > 12) return null;
  return { year, month };
}

const MONTH_LABEL = new Intl.DateTimeFormat('en-GB', {
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

/**
 * The month with no year, for the HEAD of a span whose tail carries the year —
 * `Jul – Sep 2026`. Printing the year twice in one label is the noise the dates
 * were removed for in the first place.
 */
const MONTH_SHORT = new Intl.DateTimeFormat('en-GB', { month: 'short', timeZone: 'UTC' });

/**
 * A period cut into its own months or its own quarters.
 *
 * ⚠️ **SUBDIVISIONS OF THE PERIOD, NEVER OF THE CALENDAR, AND NEVER DERIVED
 * FROM A CYCLE ROW.** F.Y. 2026-27 runs 1 May to 30 April, so its quarters are
 * May–Jul, Aug–Oct, Nov–Jan and Feb–Apr; the calendar quarters would straddle
 * two of them.
 *
 * ⚠️ *SUPERSEDED 2026-09-13, the second half of that sentence: "the `d_cycles`
 * row sharing this period's NAME runs 1 June to 31 May — a month adrift".*
 * **Cycles have run 1 APRIL to 31 MARCH since migration 117** (2026-08-30), so
 * the adrift month is the period's, not the cycle's, and the figure quoted here
 * was two months out. **The RULE is untouched and is what to keep**: this cuts
 * from the period's own dates and from nothing else, so it cannot be wrong
 * about a cycle whatever a cycle turns out to run.
 *
 * ⚠️ **And do not read the alignment as retiring the rule.** Migration
 * `182_catalogue_a_target_period_per_cycle.sql` moves every `kind: 'cycle'`
 * period onto its cycle's own window, so for those rows the two agree — but a
 * `month`, `quarter` or `year` period may carry no cycle at all and is free to
 * run months a cycle never had. **Subdividing a cycle row instead would put
 * different figures under the same label**, which is the one failure a window
 * label exists to prevent, and it stays reachable on every kind but one.
 *
 * ⚠️ **The pieces TILE the period exactly**: every day falls in exactly one
 * piece and no piece reaches outside it. That holds even when the period does
 * not begin on the first of a month or end on the last, which is why the ends
 * are clamped rather than assumed whole. A quarter is three consecutive months
 * OF THE PERIOD, so a length that is not a multiple of three ends on a short
 * quarter rather than one that overshoots.
 *
 * Returns `[]` for dates that do not parse or run backwards — a picker with no
 * subdivisions to offer, rather than a thrown error on a dashboard.
 */
export function subdivideTargetPeriod(
  period: TargetPeriod,
  kind: 'month' | 'quarter',
): TargetWindow[] {
  const start = calendarMonthOf(period.startDate);
  const end = calendarMonthOf(period.endDate);
  if (start === null || end === null) return [];
  if (period.endDate < period.startDate) return [];

  const first = start.year * 12 + (start.month - 1);
  const count = end.year * 12 + (end.month - 1) - first + 1;
  if (count < 1) return [];

  const months: TargetWindow[] = [];
  for (let index = 0; index < count; index += 1) {
    const absolute = first + index;
    const year = Math.floor(absolute / 12);
    const month = (absolute % 12) + 1;
    const monthStart = ymd(year, month, 1);
    const monthEnd = ymd(year, month, lastDayOfMonth(year, month));
    months.push({
      name: MONTH_LABEL.format(new Date(Date.UTC(year, month - 1, 1))),
      /*
       * max(monthStart, periodStart) and min(monthEnd, periodEnd). String
       * comparison is exact on `YYYY-MM-DD` and needs no `Date` at all.
       */
      from: monthStart > period.startDate ? monthStart : period.startDate,
      to: monthEnd < period.endDate ? monthEnd : period.endDate,
      periodId: null,
    });
  }

  if (kind === 'month') return months;

  const quarters: TargetWindow[] = [];
  for (let index = 0; index * 3 < months.length; index += 1) {
    const head = months[index * 3];
    const tail = months[Math.min(index * 3 + 2, months.length - 1)];
    /*
     * Both indices are in range by construction; skipping rather than asserting
     * keeps that true if the stride ever changes, and `noUncheckedIndexedAccess`
     * is what makes the check compulsory rather than defensive.
     */
    if (head === undefined || tail === undefined) continue;
    quarters.push({ name: `Q${index + 1}`, from: head.from, to: tail.to, periodId: null });
  }
  return quarters;
}

/**
 * A typed date range, or `null` when there is not one yet.
 *
 * ⚠️ **`null` covers BOTH "not filled in" and "the wrong way round",
 * deliberately: neither is a window, and the caller must not fetch for either.**
 * The two are told apart where they are WORDED, not here — a half-empty pair is
 * somebody part-way through typing, and an inverted pair is a refusal.
 */
export function dateRangeWindow(from: string, to: string): TargetWindow | null {
  if (from === '' || to === '' || from > to) return null;
  return { name: 'Custom range', from, to, periodId: null };
}

/* -------------------------------------------------------------------------
 * Target periods — the windows themselves.
 * --------------------------------------------------------------------- */

/**
 * The four kinds, in the order a picker offers them: shortest window first, and
 * `cycle` last because it is the only one that names another record.
 *
 * A real `ENUM` in the column and a `z.enum` on the write, so a fifth value is a
 * 422 rather than an unseen row — which is what makes rendering this list, and
 * only this list, correct rather than a guess.
 */
export const TARGET_PERIOD_KINDS: readonly TargetPeriodKind[] = [
  'month',
  'quarter',
  'year',
  'cycle',
];

/** Send the slug, render the label. Casing the slug is how "Won_deals" happens. */
export const TARGET_PERIOD_KIND_LABELS: Record<TargetPeriodKind, string> = {
  month: 'Month',
  quarter: 'Quarter',
  year: 'Year',
  cycle: 'Cycle',
};

export function targetPeriodKindLabel(kind: TargetPeriodKind): string {
  /*
   * Falls back to the raw slug for the same reason `targetMetricLabel` does: a
   * kind this build does not know about means the enum has been widened, and
   * showing the server's own word is what lets somebody recognise that. The
   * annotation is load-bearing — the map is exhaustive over the union, so tsc
   * would otherwise consider the `??` dead. It is not dead at runtime.
   */
  const label: string | undefined = TARGET_PERIOD_KIND_LABELS[kind];
  return label ?? kind;
}

/**
 * The one refusal in the target-periods module that carries a code.
 *
 * ⚠️ **Its sibling 409 — a duplicate `(kind, startDate, endDate)` window —
 * deliberately carries NO code**, because the useful half of that refusal is the
 * sentence naming which window collided, and no code can carry that. So the rule
 * is the same as `xero.ts`: match this code, and for anything else fall through
 * to the server's own message. Never text-match the duplicate one.
 */
export const TARGET_PERIOD_IN_USE = 'TARGET_PERIOD_IN_USE';

/**
 * A duplicate target: the same subject, metric, period, currency **and billing
 * region**.
 *
 * 🔴 **THE REGION JOINED THAT KEY WHEN TEAM TARGETS BECAME PER-REGION.**
 * *SUPERSEDED: "A duplicate target: the same subject, metric, period **and
 * currency**."* Still true of a `user` or `organisation` target, which carries
 * no region — but a team holding a `booked` target for Europe and another for
 * the UK, in one period and one currency, is now **ORDINARY** and must not be
 * reported as a clash. Copy still saying "one target per metric per period"
 * describes a key two widenings old.
 *
 * ⚠️ **The currency is in that key and was not always, and the shape repeated
 * itself exactly.** Migration 091 widened `(subject, metric, period)` to
 * include `base_currency` on the owner's ruling; the region widened it again
 * the same way, for the same reason, and both times what changed was which
 * combinations are LEGAL rather than anything about the refusal itself. **Every
 * widening of this key makes a previously-impossible row ordinary**, so a
 * client asserting a narrower uniqueness than the server enforces sends
 * somebody hunting a duplicate that is not there.
 *
 * ⚠️ **The MESSAGE is the actionable half and is not changing with the key** —
 * it names which row collided, which no code can carry. Match the code, render
 * the sentence.
 */
export const TARGET_ALREADY_EXISTS = 'TARGET_ALREADY_EXISTS';

/**
 * Whether a decimal string is one this API will take for `targetAmount`.
 *
 * Deliberately loose, and deliberately not a normaliser: the server owns the
 * real check, and rewriting `250000` to `250000.00` on the way out would save
 * something other than what was typed. Up to two decimal places, no sign — a
 * negative target is not a thing, and `-` would be refused server-side anyway
 * with a message nobody could act on.
 */
export function isTargetAmount(value: string): boolean {
  return /^\d{1,13}(\.\d{1,2})?$/.test(value.trim());
}
